import {
  purchaseProgress,
  type PurchaseOrderRow as CoreOrder,
  type ReceivedByLine,
} from '@ims/core'
import type { PurchaseOrderLineRow, PurchaseOrderRow } from '@ims/data'
import { localDay } from './format.ts'
import type { StateTone } from './sales-orders.ts'

// Purchase orders in the app: the row as core's builders take it, and the
// words for an order's state.

/** The row as clients write it (server-set columns dropped). */
export function purchaseOrderRow(row: PurchaseOrderRow): CoreOrder {
  const { created_by: _by, server_updated_at: _at, ...rest } = row
  return rest
}

/**
 * An order's state in one badge. Sent, closed and cancelled are stored;
 * "Part received" and "Received" come from the goods receipts made against it.
 */
export function purchaseOrderState(
  order: Pick<PurchaseOrderRow, 'status' | 'number'>,
  received?: {
    lines: readonly Pick<PurchaseOrderLineRow, 'id' | 'qty_ordered_milli'>[]
    by: ReceivedByLine
  },
): { label: string; tone: StateTone } {
  if (order.status === 'draft') return { label: 'Draft', tone: 'neutral' }
  // Approval (docs/plans/po-approvals.md): waiting, approved but not sent, rejected.
  if (order.status === 'submitted') return { label: 'Waiting approval', tone: 'warning' }
  if (order.status === 'approved') return { label: 'Approved', tone: 'info' }
  // Generated, numbered and frozen, not sent yet (docs/plans/po-ready.md).
  if (order.status === 'issued') return { label: 'Ready to send', tone: 'info' }
  if (order.status === 'rejected') return { label: 'Rejected', tone: 'danger' }
  if (order.status === 'cancelled') return { label: 'Cancelled', tone: 'danger' }
  if (order.status === 'closed') return { label: 'Closed', tone: 'neutral' }
  if (!order.number) return { label: 'Syncing', tone: 'warning' }
  const progress = received ? purchaseProgress(received.lines, received.by).progress : 'none'
  if (progress === 'full' || progress === 'over') return { label: 'Received', tone: 'success' }
  if (progress === 'part') return { label: 'Part received', tone: 'warning' }
  return { label: 'Sent', tone: 'info' }
}

/** What the list knows about an order: the row and what has arrived of it. */
export interface PurchaseOrderBoard {
  order: PurchaseOrderRow
  lineCount: number
  orderedMilli: number
  receivedMilli: number
}

/** The badge of an order in the list, from the summed quantities. */
export function purchaseBoardState(row: PurchaseOrderBoard): { label: string; tone: StateTone } {
  const { order, orderedMilli, receivedMilli } = row
  if (order.status !== 'sent' || !order.number) return purchaseOrderState(order)
  if (receivedMilli === 0) return { label: 'Sent', tone: 'info' }
  return receivedMilli < orderedMilli
    ? { label: 'Part received', tone: 'warning' }
    : { label: 'Received', tone: 'success' }
}

/** Whole days from `from` to `to` (both YYYY-MM-DD); negative when `to` is earlier. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)
}

/** "Due tomorrow", "Due 6 days ago", "Due 2 Kartik": when something is needed by. */
export function dueWords(
  date: string,
  today: string,
  show: (iso: string) => string,
  word = 'Due',
): { text: string; late: boolean } {
  const days = daysBetween(today, date)
  if (days < 0) return { text: `${word} ${-days} day${days === -1 ? '' : 's'} ago`, late: true }
  if (days === 0) return { text: `${word} today`, late: false }
  if (days === 1) return { text: `${word} tomorrow`, late: false }
  return { text: `${word} ${show(date)}`, late: false }
}

/** Two short lines for a list row; `tone` colours the line named by `toneOn`. */
export interface Standing {
  main: string
  sub?: string
  tone?: 'danger' | 'warning' | 'success'
  toneOn?: 'main' | 'sub'
}

/**
 * Where a purchase order stands, in two short lines: what has arrived, and when the
 * rest is needed. `show` formats a date, `amount` a quantity in milli-units.
 */
export function purchaseOrderStanding(
  row: PurchaseOrderBoard,
  today: string,
  show: (iso: string) => string,
  amount: (milli: number) => string,
): Standing {
  const { order, lineCount, orderedMilli, receivedMilli } = row
  const items = `${lineCount} item${lineCount === 1 ? '' : 's'}`
  if (order.status === 'draft') return { main: 'Not sent yet', sub: items }
  if (order.status === 'submitted')
    return { main: 'Waiting for approval', sub: items, tone: 'warning' }
  if (order.status === 'approved') return { main: 'Approved', sub: 'Not sent yet', tone: 'success' }
  if (order.status === 'issued') {
    const waited = generatedWords(order.updated_at, today)
    return {
      main: 'Not sent yet',
      sub: waited.text,
      ...(waited.late ? { tone: 'danger', toneOn: 'sub' } : {}),
    }
  }
  if (order.status === 'rejected') return { main: 'Rejected', sub: items }
  const arrived =
    receivedMilli === 0
      ? 'Nothing arrived'
      : `${amount(receivedMilli)} of ${amount(orderedMilli)} arrived`
  if (order.status === 'cancelled')
    return { main: 'Cancelled', sub: order.close_reason ?? undefined }
  if (order.status === 'closed')
    return { main: arrived, sub: order.close_reason ? `Closed: ${order.close_reason}` : 'Closed' }
  if (receivedMilli >= orderedMilli && orderedMilli > 0)
    return { main: 'All arrived', sub: items, tone: 'success' }
  if (!order.expected_date) return { main: arrived, sub: items }
  const due = dueWords(order.expected_date, today, show)
  return { main: arrived, sub: due.text, ...(due.late ? { tone: 'danger', toneOn: 'sub' } : {}) }
}

/** Days a generated order may wait unsent before the list shows it in red. */
export const UNSENT_DAYS = 3

/**
 * How long a generated order has waited unsent: "Generated today", "Generated yesterday",
 * then "Not sent for 3 days" (late). A generated order is frozen, so `updated_at` is when it
 * was generated.
 */
export function generatedWords(updatedAt: string, today: string): { text: string; late: boolean } {
  const days = Math.max(0, daysBetween(localDay(updatedAt), today))
  if (days === 0) return { text: 'Generated today', late: false }
  if (days === 1) return { text: 'Generated yesterday', late: false }
  if (days < UNSENT_DAYS) return { text: `Generated ${days} days ago`, late: false }
  return { text: `Not sent for ${days} days`, late: true }
}

/** What the editor's main button does: generate only, or generate and send. */
export type GenerateChoice = 'generate' | 'send'
const GENERATE_KEY = 'ims.po-generate'

/** The last choice on this device; generate when none was made (nothing leaves by accident). */
export function readGenerateChoice(): GenerateChoice {
  try {
    return localStorage.getItem(GENERATE_KEY) === 'send' ? 'send' : 'generate'
  } catch {
    return 'generate'
  }
}

export function saveGenerateChoice(choice: GenerateChoice): void {
  try {
    localStorage.setItem(GENERATE_KEY, choice)
  } catch {
    // Storage off (a private window): the button keeps the default.
  }
}

/**
 * A purchase order as plain text, to share with the supplier (WhatsApp, Viber, email).
 * `money` formats an amount, `amount` a quantity in milli-units.
 */
export function purchaseOrderText(
  o: {
    business: string
    number: string | null
    supplier: string | null
    neededBy: string | null
    notes: string | null
    totalPaisa: number
    lines: readonly { name: string; qtyMilli: number; unit: string; costPaisa: number }[]
  },
  money: (paisa: number) => string,
  amount: (milli: number) => string,
): string {
  const head = [
    `Purchase order ${o.number ?? ''}`.trim(),
    `From ${o.business}`,
    o.supplier && `To ${o.supplier}`,
    o.neededBy && `Needed by ${o.neededBy}`,
  ].filter(Boolean)
  const lines = o.lines.map(
    (l, i) =>
      `${i + 1}. ${l.name}: ${amount(l.qtyMilli)}${l.unit ? ` ${l.unit}` : ''} @ ${money(l.costPaisa)}`,
  )
  return [
    ...head,
    '',
    ...lines,
    '',
    `Total ${money(o.totalPaisa)}`,
    ...(o.notes ? ['', `Note: ${o.notes}`] : []),
  ].join('\n')
}
